import { useEffect, useCallback, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";

import { fetchPostsByUser } from "../redux/thunks/moderationThunks";
import {
  selectPostsByUserId,
  selectModerationLoading,
  selectModerationErrorMessage,
  selectModerationError,
} from "../redux/selectors/moderationSelectors";

/**
 * ✅ IMPROVED: Enhanced useUserPosts hook with better error handling and logging
 * 
 * This hook manages fetching and refreshing user posts with:
 * - Automatic fetching on mount and user change
 * - Prevention of infinite loops and concurrent requests
 * - Graceful handling of 404 errors (no posts yet)
 * - Comprehensive logging for debugging
 */
const useUserPosts = (userId) => {
  const dispatch = useDispatch();
  
  // 🔒 Prevent infinite loops and concurrent requests
  const hasFetchedRef = useRef(false);
  const lastUserIdRef = useRef(null);
  const isFetchingRef = useRef(false);
  const mountTimeRef = useRef(Date.now());

  // Redux state
  const posts = useSelector(selectPostsByUserId(userId));
  const loading = useSelector(selectModerationLoading);
  const error = useSelector(selectModerationError);
  const errorMessage = useSelector(selectModerationErrorMessage);

  // Local state
  const [localError, setLocalError] = useState("");
  const [lastFetchTime, setLastFetchTime] = useState(null);

  // Derived values
  const hasPosts = useMemo(
    () => Array.isArray(posts) && posts.length > 0,
    [posts]
  );
  
  const postsCount = useMemo(
    () => (Array.isArray(posts) ? posts.length : 0),
    [posts]
  );

  /* ----------------------- Handlers ----------------------- */

  /**
   * ✅ IMPROVED: Fetch user posts with comprehensive error handling
   */
  const handleFetchUserPosts = useCallback(async () => {
    if (!userId) {
      console.warn("⚠️ [useUserPosts] No userId provided");
      return null;
    }

    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      return null;
    }

    setLocalError("");
    isFetchingRef.current = true;
    
    const fetchStartTime = Date.now();

    try {
      const res = await dispatch(fetchPostsByUser(userId)).unwrap();
      
      const fetchDuration = Date.now() - fetchStartTime;
      const postCount = Array.isArray(res) ? res.length : 0;
      
      
      hasFetchedRef.current = true;
      setLastFetchTime(Date.now());
      
      return res;
    } catch (err) {
      const fetchDuration = Date.now() - fetchStartTime;
      console.error(`❌ [useUserPosts] Error after ${fetchDuration}ms:`, err);
      
      const statusCode = err?.statusCode || err?.status;
      const msg = err?.message || "Failed to fetch user posts";
      
      setLocalError(msg);
      
      // Handle different error scenarios
      if (statusCode === 404) {
        // User has no posts yet - this is not an error
        hasFetchedRef.current = true; // Mark as fetched to prevent retries
        return []; // Return empty array
      } else if (statusCode === 403) {
        // Forbidden - trying to access another user's posts
        message.error("❌ You don't have permission to view these posts");
      } else if (statusCode === 401) {
        // Unauthorized - token issue
        message.error("❌ Your session has expired. Please log in again.");
      } else {
        // Other errors
        message.error(`❌ Error: ${msg}`);
      }
      
      return null;
    } finally {
      isFetchingRef.current = false;
    }
  }, [dispatch, userId]);

  /**
   * ✅ IMPROVED: Refresh with forced refetch
   */
  const handleRefreshUserPosts = useCallback(async () => {
    hasFetchedRef.current = false; // Reset to allow refetch
    return await handleFetchUserPosts();
  }, [handleFetchUserPosts]);

  /* ----------------------- Auto-fetch Effect ----------------------- */

  useEffect(() => {
    const userChanged = lastUserIdRef.current !== userId;
    const timeSinceMount = Date.now() - mountTimeRef.current;
    
    const shouldFetch = 
      userId && 
      (userChanged || !hasFetchedRef.current) && 
      !loading && 
      !isFetchingRef.current;
    
    if (shouldFetch) {
      
      lastUserIdRef.current = userId;
      handleFetchUserPosts();
    }
  }, [userId, loading, handleFetchUserPosts]);

  /* ----------------------- Debug Info ----------------------- */

  // Log state changes in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
    }
  }, [userId, postsCount, loading, error, lastFetchTime]);

  /* ----------------------- Return ----------------------- */

  return {
    // Data
    posts,
    loading,
    error,
    errorMessage,
    localError,
    
    // Computed
    hasPosts,
    postsCount,
    lastFetchTime,
    
    // Methods (both naming conventions for flexibility)
    handleFetchUserPosts,
    handleRefreshUserPosts,
    fetchPosts: handleFetchUserPosts,
    refresh: handleRefreshUserPosts,
  };
};

export default useUserPosts;